import { i18nTranslationHelper } from '@/i18n'
import { cn } from '@/main/helpers/tailwind-helper'
import theme from '@/presentation/assets/styles/theme'
import { ValidAccountState } from '@/presentation/contexts/auth'
import { generateNameInitials } from '@/presentation/helpers/account-profile-helper'
import useAccount from '@/presentation/hooks/auth/use-account'
import { Avatar, Badge, BadgeProps, SvgIconTypeMap, Typography } from '@mui/material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { Variant } from '@mui/material/styles/createTypography'

export enum AccountAvatarSize {
  big = 'big',
  medium = 'medium',
  small = 'small'
}

type AccountAvatarAppProps = {
  size: AccountAvatarSize
  badgeIcon?: OverridableComponent<SvgIconTypeMap<object, 'svg'>>
  editable?: boolean
} & BadgeProps

const SPACING_SIZE = {
  [AccountAvatarSize.big]: theme.spacing(10),
  [AccountAvatarSize.medium]: theme.spacing(7),
  [AccountAvatarSize.small]: theme.spacing(5)
}

type TypographySizeType = {
  [key: string]: Variant
}

const TYPOGRAPHY_SIZE: TypographySizeType = {
  [AccountAvatarSize.big]: 'h4',
  [AccountAvatarSize.medium]: 'h4',
  [AccountAvatarSize.small]: 'subtitle2'
}

export default function AccountAvatarApp({
  size = AccountAvatarSize.small,
  badgeIcon: BadgeIcon,
  editable,
  className
}: AccountAvatarAppProps) {
  const t = {
    components: i18nTranslationHelper.getTComponents
  }
  const [account] = useAccount() as ValidAccountState
  const avatar = (
    <Avatar
      alt={t.components('accessibility.avatarAlt', { displayName: account.displayName })}
      src={account.pictureUrl}
      sx={{
        bgcolor: theme.palette.primary.main,
        height: SPACING_SIZE[size],
        width: SPACING_SIZE[size]
      }}
    >
      {!account.pictureUrl && (
        <Typography variant={TYPOGRAPHY_SIZE[size]} fontWeight="bold" aria-hidden="true">
          {generateNameInitials(account.displayName)}
        </Typography>
      )}
    </Avatar>
  )

  if (BadgeIcon) {
    return (
      <Badge
        className={cn(`rounded-full hover:shadow-xl ${editable && 'group'}`, className)}
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          <div
            className="flex overflow-hidden bg-[color:var(--primary-50)] rounded-full h-5 w-5
            items-center justify-start group-hover:mr-6 group-hover:w-14 p-0.5 gap-0.5 transition-all duration-300"
          >
            <BadgeIcon
              sx={{ height: theme.spacing(4 / 2), width: theme.spacing(4 / 2) }}
              color="primary"
            />
            <Typography variant="caption" color="primary" className="hidden group-hover:flex">
              {t.components('accountAvatar.edit')}
            </Typography>
          </div>
        }
      >
        {avatar}
      </Badge>
    )
  }

  return avatar
}
